import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

/** 층별 입주자·게스트 또는 역할별 직원의 권한 그룹이다. */
export interface AccessGroup {
  id: string;
  property_id: string | null;
  kind: 'RESIDENT' | 'GUEST' | 'STAFF';
  staff_role: 'ADMIN' | 'MANAGER' | 'FM' | 'HK' | 'GUARD' | 'RETAIL' | null;
  floor: number | null;
  name: string;
  origin: 'CORE' | 'LEGACY_SYNC';
  source_group_id: number | null;
  synced_at: string | null;
  revision: number;
}
export interface AccessRule {
  id: string;
  group_id: string;
  device_id: string;
  floor: number | null;
  schedule: Record<string, string[]>;
}
export interface GroupInput {
  command_id: string;
  id: string;
  property_id: string | null;
  kind: AccessGroup['kind'];
  staff_role: AccessGroup['staff_role'];
  floor: number | null;
  name: string;
}
export interface GroupRevision {
  command_id: string;
  expected_revision: number;
}
export interface RuleInput {
  id: string;
  device_id: string;
  floor: number | null;
  schedule: Record<string, string[]>;
}
export interface RuleReplacement extends GroupRevision {
  rules: RuleInput[];
}
export interface GroupResult {
  id: string;
  revision: number;
}
const record = (x: unknown): x is Record<string, unknown> =>
  !!x && typeof x === 'object' && !Array.isArray(x);
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '출입 그룹 응답을 확인할 수 없습니다.',
  });
function group(x: unknown): x is AccessGroup {
  if (!record(x)) return false;
  return (
    isUuid(x.id) &&
    (x.property_id === null || isUuid(x.property_id)) &&
    typeof x.kind === 'string' &&
    ['RESIDENT', 'GUEST', 'STAFF'].includes(x.kind) &&
    (x.staff_role === null ||
      (typeof x.staff_role === 'string' &&
        ['ADMIN', 'MANAGER', 'FM', 'HK', 'GUARD', 'RETAIL'].includes(x.staff_role))) &&
    (x.floor === null || Number.isSafeInteger(x.floor)) &&
    typeof x.name === 'string' &&
    x.name.trim().length > 0 &&
    (x.origin === 'CORE' || x.origin === 'LEGACY_SYNC') &&
    (x.source_group_id === null || Number.isSafeInteger(x.source_group_id)) &&
    (x.synced_at === null || isOffsetDateTime(x.synced_at)) &&
    Number.isSafeInteger(x.revision) &&
    Number(x.revision) > 0
  );
}
function result(x: unknown, id: string): GroupResult {
  if (!record(x) || x.id !== id || !Number.isSafeInteger(x.revision) || Number(x.revision) < 1)
    throw invalid();
  return x as unknown as GroupResult;
}
/** 현재 권한 그룹의 cursor 페이지를 조회한다. */
export async function readAccessGroups(
  client: ServiceClient,
  after?: string,
  signal?: AbortSignal,
) {
  const x = await client.get<unknown>('admin/access-groups', {
    params: { after },
    signal,
    headers: { 'Cache-Control': 'no-store' },
  });
  if (
    !record(x) ||
    !Array.isArray(x.items) ||
    !x.items.every(group) ||
    !(x.next_cursor === null || isUuid(x.next_cursor))
  )
    throw invalid();
  return { items: x.items, next_cursor: x.next_cursor };
}
export async function readAccessGroup(
  client: ServiceClient,
  id: string,
  signal?: AbortSignal,
): Promise<AccessGroup> {
  const x = await client.get<unknown>(`admin/access-groups/${id}`, {
    signal,
    headers: { 'Cache-Control': 'no-store' },
  });
  if (!group(x) || x.id !== id) throw invalid();
  return x;
}
export async function createAccessGroup(
  client: ServiceClient,
  body: GroupInput,
): Promise<AccessGroup> {
  const x = await client.post<unknown>('admin/access-groups', { json: body });
  if (!group(x) || x.id !== body.id) throw invalid();
  return x;
}
export async function updateAccessGroup(
  client: ServiceClient,
  id: string,
  body: Omit<GroupInput, 'id'> & { expected_revision: number },
): Promise<AccessGroup> {
  const x = await client.put<unknown>(`admin/access-groups/${id}`, { json: body });
  if (!group(x) || x.id !== id) throw invalid();
  return x;
}
export async function deleteAccessGroup(
  client: ServiceClient,
  id: string,
  body: GroupRevision,
): Promise<GroupResult> {
  return result(await client.delete<unknown>(`admin/access-groups/${id}`, { json: body }), id);
}
export async function readAccessGroupRules(
  client: ServiceClient,
  id: string,
  signal?: AbortSignal,
): Promise<AccessRule[]> {
  const x = await client.get<unknown>(`admin/access-groups/${id}/rules`, {
    signal,
    headers: { 'Cache-Control': 'no-store' },
  });
  if (
    !record(x) ||
    !Array.isArray(x.items) ||
    !x.items.every(
      (r: unknown) =>
        record(r) &&
        isUuid(r.id) &&
        r.group_id === id &&
        isUuid(r.device_id) &&
        (r.floor === null || Number.isSafeInteger(r.floor)) &&
        record(r.schedule) &&
        Object.values(r.schedule).every(
          (v) => Array.isArray(v) && v.every((s) => typeof s === 'string'),
        ),
    )
  )
    throw invalid();
  return x.items as AccessRule[];
}
export async function replaceAccessGroupRules(
  client: ServiceClient,
  id: string,
  body: RuleReplacement,
): Promise<GroupResult> {
  return result(await client.put<unknown>(`admin/access-groups/${id}/rules`, { json: body }), id);
}
